import { useMemo } from 'react'

import {
  GetBenchmarkRoleMappingsDocument,
  GetMercerSalaryDataDocument,
  GetMercerSurveyRegistriesDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { BenchmarkMatchResult } from 'modules/job_catalog/benchmark/types'
import { toDominanceFlag, toNumber } from 'modules/job_catalog/benchmark/lib/parsers'

export const useBenchmarkData = (treeNodeUuid: string, enabled = true) => {
  const { data: mappingsData, loading: mappingsLoading } = useQuery(
    GetBenchmarkRoleMappingsDocument,
    {
      variables: { treeNodeUuids: [treeNodeUuid], activeOnly: true },
      skip: !enabled || !treeNodeUuid,
    }
  )

  const activeMapping = useMemo(
    () => mappingsData?.jobCatalog?.benchmarkRoleMappingsConnection?.nodes?.[0] ?? null,
    [mappingsData]
  )

  const { data: salaryData, loading: salaryLoading } = useQuery(GetMercerSalaryDataDocument, {
    variables: {
      mercerJobCodes: activeMapping ? [activeMapping.externalJobCode] : [],
    },
    skip: !enabled || !activeMapping,
  })

  const salaryNode = useMemo(() => {
    const nodes = salaryData?.jobCatalog?.mercerSalaryDataConnection?.nodes ?? []
    if (nodes.length === 0) return null

    return (
      [...nodes].sort((left, right) => {
        const leftTs = left?.updatedAt ? new Date(left.updatedAt).getTime() : 0
        const rightTs = right?.updatedAt ? new Date(right.updatedAt).getTime() : 0
        return rightTs - leftTs
      })[0] ?? null
    )
  }, [salaryData])

  const surveyCode = useMemo(() => salaryNode?.surveyCode ?? null, [salaryNode])

  const { data: registryData, loading: registryLoading } = useQuery(
    GetMercerSurveyRegistriesDocument,
    {
      variables: { surveyCodes: surveyCode ? [surveyCode] : [] },
      skip: !enabled || !surveyCode,
    }
  )

  const registry = useMemo(
    () => registryData?.jobCatalog?.mercerSurveyRegistriesConnection?.nodes?.[0] ?? null,
    [registryData]
  )

  const data: BenchmarkMatchResult = useMemo(() => {
    if (!enabled) {
      return { status: 'unmapped', companySalaryRange: null }
    }

    if (!activeMapping) {
      return { status: 'unmapped', companySalaryRange: null }
    }

    if (!salaryNode) {
      if (salaryLoading) {
        return { status: 'no_data', companySalaryRange: null }
      }
      return { status: 'no_data', companySalaryRange: null }
    }

    const obsCount = salaryNode.totalGuaranteedAnnualNumObs
    if (obsCount == null || obsCount < 5) {
      return { status: 'suppressed', companySalaryRange: null, obsCount: obsCount ?? null }
    }

    return {
      status: 'matched',
      companySalaryRange: null,
      tgaP25: toNumber(salaryNode.totalGuaranteedAnnualPercent25),
      tgaP50: toNumber(salaryNode.totalGuaranteedAnnualMedian),
      tgaP75: toNumber(salaryNode.totalGuaranteedAnnualPercent75),
      tgaMean: toNumber(salaryNode.totalGuaranteedAnnualMean),
      orgCount: salaryNode.totalGuaranteedAnnualOrgs ?? null,
      obsCount: salaryNode.totalGuaranteedAnnualNumObs ?? null,
      distinctOrgCount: salaryNode.totalGuaranteedAnnualDistOrgs ?? null,
      dominanceFlag: toDominanceFlag(salaryNode.totalGuaranteedAnnualDominance),
      currency: registry?.currency ?? null,
      year: registry?.year ?? null,
      countryCode: registry?.countryCode ?? null,
    }
  }, [activeMapping, enabled, registry, salaryLoading, salaryNode])

  return {
    data,
    loading: enabled && (mappingsLoading || salaryLoading || registryLoading),
  }
}
